import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../_shared/types/channel.type';

export type TGetListAppStoreChannelResponse = ApiResponse<IPagination<TChannel>>;
export type TGetListAppStoreChannelParams = FetchParams;

export const getListAppStoreChannel = async (
  params: TGetListAppStoreChannelParams
): Promise<TGetListAppStoreChannelResponse> => {
  const url = '/integration/appstore/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_APP_STORE_CHANNEL_QUERY_KEY = 'get-list-app-store-channel';

export const getListAppStoreChannelQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetListAppStoreChannelParams,
  select?: (data: TGetListAppStoreChannelResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_APP_STORE_CHANNEL_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getListAppStoreChannel(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListAppStoreChannelQuery<TData>(options: {
  params: TGetListAppStoreChannelParams;
  queryConfig?: QueryConfig<typeof getListAppStoreChannelQueryOptions<TData>>;
  select: (data: TGetListAppStoreChannelResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListAppStoreChannelQuery(options: {
  params: TGetListAppStoreChannelParams;
  queryConfig?: QueryConfig<typeof getListAppStoreChannelQueryOptions<TGetListAppStoreChannelResponse>>;
}): ReturnType<typeof useQuery<TGetListAppStoreChannelResponse>>;

// Implementation
export function useGetListAppStoreChannelQuery<TData = TGetListAppStoreChannelResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListAppStoreChannelParams;
  queryConfig?: QueryConfig<typeof getListAppStoreChannelQueryOptions<TData | TGetListAppStoreChannelResponse>>;
  select?: (data: TGetListAppStoreChannelResponse) => TData;
}) {
  return useQuery({
    ...getListAppStoreChannelQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListAppStoreChannelResponse ? TGetListAppStoreChannelResponse : TData>
  >;
}
